import { Stack, Typography } from '@mui/material';
import {
  FilterKeys,
  Product,
  ProductTypeId,
  SchoolYear,
  SORT_DIRECTION,
  useGetProductsListQuery,
} from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { ProductSelectRow } from './ProductSelectRow';
import { ProductTagSelect, ProductTagSelectProps } from './ProductTagSelect';
import { ProductTypeTagSelect, ProductTypeTagSelectProps } from './ProductTypeTagSelect';

type ProductExpandedSelectProps = PropsWithChildren<{
  schoolId: string;
  selectedProductIds: string[];
  selectedProductTypeIds: ProductTypeId[];
  onApply: (productIds: string[], productTypeIds: ProductTypeId[]) => void;
  onClear: () => void;
  onClose: () => void;
  schoolYear?: SchoolYear;
}>;
export const ProductExpandedSelect: FC<ProductExpandedSelectProps> = ({
  schoolId,
  selectedProductIds: initialSelectedProductIds,
  selectedProductTypeIds: initialSelectedProductTypeIds,
  onApply,
  onClose,
  onClear,
  schoolYear,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedProductsAndProductTypes, setSelectedProductsAndProductTypes] = useState({
    productIds: initialSelectedProductIds,
    productTypeIds: initialSelectedProductTypeIds,
  });

  const { isLoading, data, params, isFetchingNextPage, fetchNextPage, hasNextPage, setParams } =
    useGetProductsListQuery(
      {
        schoolId,
        query: '',
        sort: { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
        filters: schoolYear ? { [FilterKeys.YearId]: [schoolYear.id] } : undefined,
      },
      { refetchOnMount: 'always' },
    );

  const toggleProductId = useCallback((v: string) => {
    setSelectedProductsAndProductTypes((filters) => ({
      productTypeIds: (filters.productTypeIds || []).filter((id) => id.productId !== v),
      productIds: filters.productIds?.includes(v)
        ? filters.productIds?.filter((ct) => ct !== v)
        : [...(filters.productIds || []), v],
    }));
  }, []);

  const toggleProductTypeId = useCallback((v: ProductTypeId) => {
    setSelectedProductsAndProductTypes((filters) => ({
      productIds: (filters.productIds || []).filter((id) => id !== v.productId),
      productTypeIds: filters.productTypeIds?.some((p) => p.isEqual(v))
        ? filters.productTypeIds?.filter((p) => !p.isEqual(v))
        : [...(filters.productTypeIds || []), v],
    }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(
      selectedProductsAndProductTypes.productIds,
      selectedProductsAndProductTypes.productTypeIds,
    );
    onClose();
  }, [selectedProductsAndProductTypes, onApply, onClose]);

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  useEffect(() => {
    setParams((p) => ({
      ...p,
      filters: schoolYear ? { [FilterKeys.YearId]: [schoolYear.id] } : undefined,
    }));
  }, [schoolYear, setParams]);

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const entries =
      data.pages.reduce((prev, curr) => [...prev, ...curr.results], [] as Product[]) ?? [];

    if (!entries.length)
      return (
        <Stack m={1} gap={2}>
          <Typography p={1}>
            <FormattedMessage id="input-NoOptionsFound" />
          </Typography>
          <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
        </Stack>
      );

    const handleSelectProductType = (typeId: ProductTypeId) => {
      const relatedProduct = entries.find((p) => p.id === typeId.productId);

      if (
        relatedProduct &&
        selectedProductsAndProductTypes.productTypeIds.filter(
          (type) => type.productId === relatedProduct.id,
        ).length +
          1 ===
          relatedProduct.types.length &&
        !selectedProductsAndProductTypes.productTypeIds.some((id) => id.isEqual(typeId))
      ) {
        toggleProductId(relatedProduct.id);
        return;
      }

      toggleProductTypeId(typeId);
    };

    return (
      <Stack mb={2} gap={1}>
        {entries.map((product) => (
          <ProductSelectRow
            schoolYear={schoolYear}
            onSelectProduct={(id) => {
              toggleProductId(id);
              handleChangeQuery('');
            }}
            onSelectProductType={handleSelectProductType}
            key={product.id}
            product={product}
            selectedProductTypes={selectedProductsAndProductTypes.productTypeIds.filter(
              (id) => id.productId === product.id,
            )}
            isSelected={selectedProductsAndProductTypes.productIds.includes(product.id)}
          />
        ))}
        {hasNextPage && <SelectContentSkeleton />}
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [
    data,
    selectedProductsAndProductTypes,
    toggleProductTypeId,
    toggleProductId,
    handleChangeQuery,
    handleApply,
    onClear,
    hasNextPage,
    schoolYear,
  ]);

  return (
    <ExpandedSelect
      hasSelectedValue={
        selectedProductsAndProductTypes.productIds.length > 0 ||
        selectedProductsAndProductTypes.productTypeIds.length > 0
      }
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
      onClickInputArea={() => inputRef.current?.focus()}
      isFetchingNextPage={isLoading || isFetchingNextPage}
      hasNextPage={hasNextPage}
      onFetchNextPage={fetchNextPage}
      width={400}
    >
      {renderProductTags({
        ids: selectedProductsAndProductTypes.productIds,
        tagProps: { sx: { maxWidth: 200 }, size: 'small' },
        onDelete: toggleProductId,
      })}
      {renderProductTypeTags({
        ids: selectedProductsAndProductTypes.productTypeIds,
        tagProps: { sx: { maxWidth: 200 }, size: 'small' },
        onDelete: toggleProductTypeId,
      })}

      <SelectSearchInput
        ref={inputRef}
        autoFocus
        value={params.query || ''}
        onChangeText={handleChangeQuery}
      />
    </ExpandedSelect>
  );
};

type RenderProductTagsParams = {
  ids: string[];
  onDelete?: (v: string) => void;
  onClick?: (v: string) => void;
  tagProps?: Omit<ProductTagSelectProps, 'id'> | ((v: string) => Omit<ProductTagSelectProps, 'id'>);
};

export const renderProductTags = ({ ids, onDelete, onClick, tagProps }: RenderProductTagsParams) =>
  ids.map((v) => (
    <ProductTagSelect
      key={v}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));

type RenderProductTypeTagsParams = {
  ids: ProductTypeId[];
  onDelete?: (v: ProductTypeId) => void;
  onClick?: (v: ProductTypeId) => void;
  tagProps?:
    | Omit<ProductTypeTagSelectProps, 'id'>
    | ((v: ProductTypeId) => Omit<ProductTypeTagSelectProps, 'id'>);
};

export const renderProductTypeTags = ({
  ids,
  onDelete,
  onClick,
  tagProps,
}: RenderProductTypeTagsParams) =>
  ids.map((v) => (
    <ProductTypeTagSelect
      key={v.toString()}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
